import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, IconButton, Collapse, CircularProgress, Alert, Select, MenuItem,
  FormControl, InputLabel,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { fetchPurchaseOrders, PurchaseOrder } from '../../store/slices/supplyChainSlice';
import { AppDispatch, RootState } from '../../store/store';

const statusColor: Record<string, any> = {
  open: 'primary',
  partiallyReceived: 'warning',
  fullyReceived: 'success',
  cancelled: 'default',
};

const PORow: React.FC<{ po: PurchaseOrder }> = ({ po }) => {
  const [open, setOpen] = useState(false);
  const isOverdue = po.status === 'open' && po.expectedReceiptDate && new Date(po.expectedReceiptDate) < new Date();
  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <strong>{po.poNumber}</strong>
          {isOverdue && <Chip label="OVERDUE" size="small" color="error" sx={{ ml: 1 }} />}
        </TableCell>
        <TableCell>{po.vendorName}</TableCell>
        <TableCell>
          <Chip label={po.status} size="small" color={statusColor[po.status] || 'default'} />
        </TableCell>
        <TableCell>{po.orderDate ? new Date(po.orderDate).toLocaleDateString() : '—'}</TableCell>
        <TableCell>{po.expectedReceiptDate ? new Date(po.expectedReceiptDate).toLocaleDateString() : '—'}</TableCell>
        <TableCell align="center">{po.lines?.length ?? 0}</TableCell>
        <TableCell align="right">{po.totalAmount != null ? `$${Number(po.totalAmount).toLocaleString()}` : '—'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>Line Items</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Ordered</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell>Expected</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(po.lines || []).map((line, i) => (
                    <TableRow key={line.id || i}>
                      <TableCell>{line.itemCode}</TableCell>
                      <TableCell>{line.itemDescription}</TableCell>
                      <TableCell align="right">{line.orderedQuantity}</TableCell>
                      <TableCell align="right">{line.receivedQuantity}</TableCell>
                      <TableCell>{line.expectedDate ? new Date(line.expectedDate).toLocaleDateString() : '—'}</TableCell>
                      <TableCell><Chip label={line.lineStatus} size="small" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const PurchaseOrders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { purchaseOrders } = useSelector((s: RootState) => s.supplyChain);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    dispatch(fetchPurchaseOrders(statusFilter ? { status: statusFilter } : {}));
  }, [dispatch, statusFilter]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Purchase Orders</Typography>

      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="partiallyReceived">Partially Received</MenuItem>
            <MenuItem value="fullyReceived">Fully Received</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {purchaseOrders.error && <Alert severity="error" sx={{ mb: 2 }}>{purchaseOrders.error}</Alert>}
      {purchaseOrders.loading ? <CircularProgress /> : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>PO Number</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Expected Receipt</TableCell>
              <TableCell align="center"># Lines</TableCell>
              <TableCell align="right">Total Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchaseOrders.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">No purchase orders found</Typography>
                </TableCell>
              </TableRow>
            ) : purchaseOrders.data.map(po => <PORow key={po.id} po={po} />)}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default PurchaseOrders;
